import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, Dropdown, Modal, Table, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import type {
  SorterResult,
  TableCurrentDataSource
} from 'antd/lib/table/interface';
import {
  DownloadOutlined,
  MoreOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import {
  cancelInvoice,
  exportInvoicesCsv,
  fetchInvoicePdf,
  fetchInvoices
} from 'services/api/invoicing';
import { invoicePayUrl } from 'constants/invoicing';
import {
  Invoice,
  InvoiceDocumentType,
  InvoiceListFilters,
  InvoicePaymentMethod,
  InvoiceSortBy,
  InvoiceStatus
} from 'types/api/invoicing';

import { showErrorMessage } from 'utils/showErrorMessage';
import { PageWrapper } from 'components/ui';
import Filters from './components/Filters';
import MarkPaidModal from './components/MarkPaidModal';
import NumberingModal from './components/NumberingModal';
import { INVOICE_COLUMNS } from '../invoiceColumns';
import { confirmAndSendInvoice } from '../sendInvoiceWithToast';

const PAGE_LIMIT = 20;

const Invoices: FC = () => {
  const { modal, message } = App.useApp();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<InvoiceListFilters>({});
  const [sort, setSort] = useState<
    Pick<InvoiceListFilters, 'sortBy' | 'sortOrder'>
  >({});

  const [page, setPage] = useState(1);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfOpen, setPdfOpen] = useState(false);
  const [markPaid, setMarkPaid] = useState<Invoice | null>(null);
  const [numberingOpen, setNumberingOpen] = useState(false);

  const reload = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchInvoices({
          ...filters,
          ...sort,
          page,
          limit: PAGE_LIMIT
        });

        if (cancelled) return;
        setInvoices(res.data);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [filters, sort, page, refreshKey]);

  const onApplyFilters = useCallback((next: InvoiceListFilters) => {
    setPage(1);
    setFilters(next);
  }, []);

  const onCopyPayLink = useCallback(
    (url: string) => {
      copy(url);
      message.success('Payment link copied');
    },
    [message]
  );

  // Server-side sort — the BE whitelists sortBy to id/invoiceNumber/createdAt/
  // dueDate/totalAmount/status (camelCase) + sortOrder ASC|DESC.
  const onTableChange = useCallback(
    (
      _pagination: unknown,
      _tableFilters: unknown,
      sorter: SorterResult<Invoice> | SorterResult<Invoice>[],
      extra: TableCurrentDataSource<Invoice> & { action: string }
    ) => {
      if (extra.action !== 'sort') {
        return;
      }

      const s = Array.isArray(sorter) ? sorter[0] : sorter;

      setSort(
        s && s.order
          ? {
              sortBy: s.field as InvoiceSortBy,
              sortOrder: s.order === 'ascend' ? 'ASC' : 'DESC'
            }
          : {}
      );
      setPage(1);
    },
    []
  );

  const onViewPdf = useCallback(async (invoice: Invoice) => {
    try {
      const blob = await fetchInvoicePdf(invoice.publicId);
      setPdfUrl(URL.createObjectURL(blob));
      setPdfOpen(true);
    } catch (err) {
      showErrorMessage(err);
    }
  }, []);

  const closePdf = () => {
    setPdfOpen(false);

    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }

    setPdfUrl('');
  };

  const onCancel = useCallback(
    (invoice: Invoice) => {
      modal.confirm({
        title: `Cancel invoice ${invoice.invoiceNumber ?? ''}?`,
        okText: 'Cancel invoice',
        okButtonProps: { danger: true },
        cancelText: 'Close',
        onOk: async () => {
          try {
            await cancelInvoice(invoice.publicId);
            reload();
          } catch (err) {
            showErrorMessage(err);
          }
        }
      });
    },
    [modal, reload]
  );

  const onExportCsv = useCallback(async () => {
    try {
      const blob = await exportInvoicesCsv(filters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'invoices.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showErrorMessage(err);
    }
  }, [filters]);

  const columns: ColumnProps<Invoice>[] = useMemo(() => {
    const sortCol = (
      field: InvoiceSortBy
    ): Pick<ColumnProps<Invoice>, 'sorter' | 'sortOrder'> => ({
      sorter: true,
      sortOrder:
        // eslint-disable-next-line no-nested-ternary
        sort.sortBy === field
          ? sort.sortOrder === 'ASC'
            ? 'ascend'
            : 'descend'
          : null
    });

    return [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 80, ...sortCol('id') },
      {
        title: 'Hashed ID',
        dataIndex: 'publicId',
        key: 'publicId',
        width: 130,
        render: (value: string) => (
          <Typography.Text
            copyable={{ text: value }}
            style={{ fontFamily: 'monospace' }}
          >
            {value.slice(0, 8)}…
          </Typography.Text>
        )
      },
      { ...INVOICE_COLUMNS.invoiceNumber, ...sortCol('invoiceNumber') },
      {
        title: 'Patient',
        dataIndex: 'patientName',
        key: 'patientName',
        ellipsis: true
      },
      { ...INVOICE_COLUMNS.status, ...sortCol('status') },
      INVOICE_COLUMNS.paymentMethod,
      { ...INVOICE_COLUMNS.total, ...sortCol('totalAmount') },
      { ...INVOICE_COLUMNS.due, ...sortCol('dueDate') },
      { ...INVOICE_COLUMNS.created, ...sortCol('createdAt') },
      {
        title: '',
        key: 'actions',
        align: 'right',
        width: 60,
        fixed: 'right',
        render: (_, row) => {
          // A credit note is not payable and cannot be cancelled — it is
          // reversed with another credit note.
          const isCreditNote =
            row.documentType === InvoiceDocumentType.CreditNote;

          const canCancel =
            !isCreditNote &&
            row.status !== InvoiceStatus.Paid &&
            row.status !== InvoiceStatus.Cancelled;

          // Manual mark-paid is allowed on any non-paid, non-cancelled
          // invoice (the BE permits it regardless of payment method).
          const canMarkPaid = canCancel;

          // Pay link is meaningful for an issued stripe invoice. Resolved by
          // invoicePayUrl: the Stripe-hosted page when the invoice carries
          // one, else the on-domain page.
          const canCopyPay =
            !isCreditNote &&
            row.status === InvoiceStatus.Issued &&
            row.paymentMethod === InvoicePaymentMethod.Stripe;

          // Send/resend is meaningful once issued or paid (drafts 400).
          const canSend =
            row.status === InvoiceStatus.Issued ||
            row.status === InvoiceStatus.Paid;

          const items: MenuProps['items'] = [
            { key: 'view', label: 'View' },
            { key: 'pdf', label: 'PDF' },
            ...(canCopyPay ? [{ key: 'copy', label: 'Copy pay link' }] : []),
            ...(canSend ? [{ key: 'send', label: 'Send' }] : []),
            ...(canMarkPaid ? [{ key: 'paid', label: 'Mark paid' }] : []),
            ...(canCancel
              ? [{ key: 'cancel', label: 'Cancel', danger: true }]
              : [])
          ];

          const onMenuClick: MenuProps['onClick'] = ({ key }) => {
            if (key === 'view') {
              navigate(`/invoicing/invoices/${row.publicId}`);
            } else if (key === 'pdf') {
              onViewPdf(row);
            } else if (key === 'copy') {
              onCopyPayLink(invoicePayUrl(row));
            } else if (key === 'send') {
              confirmAndSendInvoice(String(row.id), {
                modal,
                message,
                invoiceNumber: row.invoiceNumber,
                email: row.patientEmail
              });
            } else if (key === 'paid') {
              setMarkPaid(row);
            } else if (key === 'cancel') {
              onCancel(row);
            }
          };

          return (
            <Dropdown
              menu={{ items, onClick: onMenuClick }}
              trigger={['click']}
            >
              <Button size="small" icon={<MoreOutlined />} />
            </Dropdown>
          );
        }
      }
    ];
  }, [navigate, onViewPdf, onCancel, onCopyPayLink, modal, message, sort]);

  return (
    <PageWrapper
      title="Invoices"
      color="white"
      extra={[
        <Button
          key="new"
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/invoicing/invoices/create')}
        >
          New invoice
        </Button>,
        <Button key="csv" icon={<DownloadOutlined />} onClick={onExportCsv}>
          Export CSV
        </Button>,
        // Shown to every admin for now. The BE still guards the counter
        // endpoints with @Roles('super_admin'), so an ordinary admin can open
        // the modal and read it but gets a 403 on save — the modal surfaces
        // that message rather than failing silently.
        <Button key="numbering" onClick={() => setNumberingOpen(true)}>
          Numbering
        </Button>
      ]}
    >
      <Filters onSubmit={onApplyFilters} />
      <Table
        dataSource={invoices}
        columns={columns}
        loading={loading}
        rowKey="publicId"
        onChange={onTableChange}
        scroll={{ x: 'max-content' }}
        pagination={{
          total,
          current: page,
          pageSize: PAGE_LIMIT,
          onChange: setPage,
          showSizeChanger: false
        }}
      />
      <Modal
        open={pdfOpen}
        onCancel={closePdf}
        footer={null}
        width={900}
        title="Invoice PDF"
      >
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            title="Invoice PDF"
            style={{ width: '100%', height: '75vh', border: 'none' }}
          />
        )}
      </Modal>
      <MarkPaidModal
        invoice={markPaid}
        onClose={() => setMarkPaid(null)}
        onSuccess={reload}
      />
      <NumberingModal
        open={numberingOpen}
        onClose={() => setNumberingOpen(false)}
      />
    </PageWrapper>
  );
};

export default Invoices;
